import React from 'react';
import translate from '../../translate';
import { LocationDto } from '../../../store/locationsSlice';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router';
import { RequestStatus } from '../../../store/network/managedRequest';
import { PARTNER_MANAGER_DETAILS_TYPE } from '../../../util/enums';
import {
    LocationApaleoLinkStatus,
    PropertyLinkInfoDto,
    RequestOAuthLandingPageForLocation,
} from '../../../store/propertyLinkSlice';
import FieldSelectPmsProvider from '../../profile/forms/field-select-pms-provider';
import { APP_LINK } from '../../../constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCircle } from '@fortawesome/pro-light-svg-icons';
import { PbGenericRequest } from '../../../store/pbRequestSlice';

const HOTEL_LINK_PATTERN =
    '{{BASE}}/n/auth/guest_login/guest_login?hotel_code={{HOTEL_CODE}}';

function buildHotelLink(hotelCode: string): string {
    return HOTEL_LINK_PATTERN.replace('{{BASE}}', APP_LINK).replace(
        '{{HOTEL_CODE}}',
        hotelCode
    );
}

const SHOW_BUTTON = true;
const NO_BUTTON = false;

const STYLE_DONE_TASK = { opacity: 0.6 };
const STYLE_FUTURE_TASK = { opacity: 0.6 };

export interface Props {
    strings: {
        enterEmailForOAuthLandingPageRequest: string;
        requestOAuthLandingPageForLocation: string;
        requestNewOAuthLandingPageForLocation: string;
        hotelCodeDescription: string;
        hotelCodeLinkDescription: string;
        propertyLinkCodeDescription: string;
        propertyLinkUrlDescription: string;
        copyPropertyLinkUrl: string;
        propertyManagementSystemProviderDescription: string;
        apaleoStepsTitle: string;
        apaleoStepsLocationExists: string;
        apaleoStepsCreateLink: string;
        apaleoStepsWaitForPartner: string;
        apaleoStepsAssignProperty: string;
        descriptionWaitForPartner: string;
        linkToSelectProperty: string;
    };
    requestApaleoLandingPageRequest: PbGenericRequest;
    requestApaleoLandingPageData: PropertyLinkInfoDto | undefined;
    getPropertyLinkInfoRequest: PbGenericRequest;
    getPropertyLinkInfoData: PropertyLinkInfoDto | undefined;
    location: LocationDto;
    onLandingPageEmailButtonClicked: (
        requestPayload: RequestOAuthLandingPageForLocation
    ) => void;
    email?: string;
}

function isGuestStayLocation(location: LocationDto) {
    return location.contractual_type === PARTNER_MANAGER_DETAILS_TYPE.RMS;
}

export function shouldRenderApaleoLinkForm(location: LocationDto): boolean {
    return isGuestStayLocation(location);
}

function shouldRenderHotelCode(location: LocationDto): boolean {
    return isGuestStayLocation(location);
}

function shouldRenderPropertyOAuthLinkSection(location: LocationDto): boolean {
    return isGuestStayLocation(location);
}

function OAuthPropertyLinkForm(props: Readonly<Props>) {
    const anyRequestPending =
        props.requestApaleoLandingPageRequest.status ===
            RequestStatus.PENDING ||
        props.getPropertyLinkInfoRequest.status === RequestStatus.PENDING;

    const location = props.location;
    const propertyLinkInfo =
        props.requestApaleoLandingPageData ?? props.getPropertyLinkInfoData;
    const propertyLinkStatus =
        propertyLinkInfo?.status || LocationApaleoLinkStatus.INITIAL;

    function renderPropertyLinkInfo(showButton: boolean) {
        const inputId = `${location.id}-property-link-url`;
        return (
            <div className="form-group">
                <label htmlFor={inputId}>
                    {props.strings.propertyLinkUrlDescription}
                </label>
                <div className="form-line">
                    <input
                        type="text"
                        className="form-control"
                        id={inputId}
                        value={propertyLinkInfo ? propertyLinkInfo.url : ''}
                        readOnly
                    />
                </div>
                {showButton &&
                    renderRequestOAuthLandingPageButton(
                        props.strings.requestOAuthLandingPageForLocation
                    )}
            </div>
        );
    }

    function renderRequestOAuthLandingPageButton(text: string) {
        return (
            <Button
                disabled={anyRequestPending}
                className="btn btn-outline-secondary"
                onClick={() => {
                    props.onLandingPageEmailButtonClicked({
                        email: props.email,
                        location_id: location.id,
                    });
                }}
            >
                {anyRequestPending ? '...' : text}
            </Button>
        );
    }

    const renderHotelCode = () => {
        const hotelCodeLink = buildHotelLink(location.hotel_code);
        if (shouldRenderHotelCode(location)) {
            return (
                <div className="form-group">
                    <label htmlFor="">
                        {props.strings.hotelCodeDescription}
                    </label>
                    <div className="form-line">
                        <input
                            type="text"
                            className="form-control"
                            id=""
                            value={location.hotel_code}
                            disabled
                        />
                    </div>
                    <div className="form-line">
                        <label>{props.strings.hotelCodeLinkDescription}</label>
                        <input
                            type="text"
                            className="form-control"
                            value={hotelCodeLink}
                            readOnly
                        />
                    </div>
                </div>
            );
        } else {
            return null;
        }
    };
    const renderPropertyManagementProvider = () => {
        return (
            <FieldSelectPmsProvider
                label={
                    props.strings.propertyManagementSystemProviderDescription
                }
                value={location.property_management_system_provider}
                disabled
                onChange={() => {}}
            />
        );
    };

    return (
        <div>
            {renderHotelCode()}
            {renderPropertyManagementProvider()}
            <hr className={'divider'} />
            {renderApaleoConnectStage()}
        </div>
    );

    function renderApaleoConnectStage() {
        if (!shouldRenderPropertyOAuthLinkSection(location)) {
            return null;
        }

        switch (propertyLinkStatus) {
            case LocationApaleoLinkStatus.INITIAL:
                return renderInitialApaleoStage();
            case LocationApaleoLinkStatus.CODE_GENERATED:
                return renderCodeGeneratedApaleoStage();
            case LocationApaleoLinkStatus.ACCESS_GRANTED:
                return renderAccessGrantedApaleoStage();
            case LocationApaleoLinkStatus.COMPLETED:
                return renderCompletedApaleoStage();
        }
        return null;
    }

    function renderInitialApaleoStage() {
        return (
            <div className={'mt-10'}>
                <h2 className={'mb-10 headline1'}>
                    <span>{props.strings.apaleoStepsTitle}</span>
                </h2>
                <p style={STYLE_DONE_TASK}>
                    {renderDoneCheck()}{' '}
                    {props.strings.apaleoStepsLocationExists}
                </p>
                <p>
                    {renderOpenCheck()} {props.strings.apaleoStepsCreateLink}
                </p>
                <div className={'ml-20'}>
                    {renderPropertyLinkInfo(SHOW_BUTTON)}
                </div>
                <p style={STYLE_FUTURE_TASK}>
                    {renderOpenCheck()}{' '}
                    {props.strings.apaleoStepsWaitForPartner}
                </p>
                <p style={STYLE_FUTURE_TASK}>
                    {renderOpenCheck()}{' '}
                    {props.strings.apaleoStepsAssignProperty}
                </p>
            </div>
        );
    }

    function renderCodeGeneratedApaleoStage() {
        return (
            <div className={'mt-10'}>
                <h2 className={'mb-10 headline1'}>
                    <span>{props.strings.apaleoStepsTitle}</span>
                </h2>
                <p style={STYLE_DONE_TASK}>
                    {renderDoneCheck()}{' '}
                    {props.strings.apaleoStepsLocationExists}
                </p>
                <p style={STYLE_DONE_TASK}>
                    {renderDoneCheck()} {props.strings.apaleoStepsCreateLink}
                </p>
                <div className={'ml-20'}>
                    {renderPropertyLinkInfo(NO_BUTTON)}
                </div>
                <p>
                    {renderOpenCheck()}{' '}
                    {props.strings.apaleoStepsWaitForPartner}
                </p>
                <div className={'ml-20'}>
                    <p>{props.strings.descriptionWaitForPartner}</p>
                    {renderRequestOAuthLandingPageButton(
                        props.strings.requestNewOAuthLandingPageForLocation
                    )}
                </div>
                <p style={STYLE_FUTURE_TASK}>
                    {renderOpenCheck()}{' '}
                    {props.strings.apaleoStepsAssignProperty}
                </p>
            </div>
        );
    }

    function renderAccessGrantedApaleoStage() {
        return (
            <div className={'mt-10'}>
                <h2 className={'mb-10 headline1'}>
                    <span>{props.strings.apaleoStepsTitle}</span>
                </h2>
                <p style={STYLE_DONE_TASK}>
                    {renderDoneCheck()}{' '}
                    {props.strings.apaleoStepsLocationExists}
                </p>
                <p style={STYLE_DONE_TASK}>
                    {renderDoneCheck()} {props.strings.apaleoStepsCreateLink}
                </p>
                <p style={STYLE_DONE_TASK}>
                    {renderDoneCheck()}{' '}
                    {props.strings.apaleoStepsWaitForPartner}
                </p>
                <p>
                    {renderOpenCheck()}{' '}
                    {props.strings.apaleoStepsAssignProperty}
                </p>
                <div className={'ml-20'}>{renderAssignProperty()}</div>
            </div>
        );
    }

    function renderCompletedApaleoStage() {
        return (
            <div className={'mt-10'}>
                <h2 className={'mb-10 headline1'}>
                    <span>{props.strings.apaleoStepsTitle}</span>
                </h2>
                <p style={STYLE_DONE_TASK}>
                    {renderDoneCheck()}{' '}
                    {props.strings.apaleoStepsLocationExists}
                </p>
                <p style={STYLE_DONE_TASK}>
                    {renderDoneCheck()} {props.strings.apaleoStepsCreateLink}
                </p>
                <p style={STYLE_DONE_TASK}>
                    {renderDoneCheck()}{' '}
                    {props.strings.apaleoStepsWaitForPartner}
                </p>
                <p style={STYLE_DONE_TASK}>
                    {renderDoneCheck()}{' '}
                    {props.strings.apaleoStepsAssignProperty}
                </p>
            </div>
        );
    }

    function renderDoneCheck() {
        return (
            <FontAwesomeIcon icon={faCheckCircle} className="icon-for-button" />
        );
    }

    function renderOpenCheck() {
        return <FontAwesomeIcon icon={faCircle} className="icon-for-button" />;
    }

    function renderAssignProperty() {
        return (
            <div>
                <Link
                    to={`/objects/${props.location.mansion_id}/${props.location.id}/apaleo-property`}
                    className="icon icon-wew-button-enter icon-sm"
                >
                    {' '}
                    {props.strings.linkToSelectProperty}
                </Link>
            </div>
        );
    }
}

export default translate('OAuthPropertyLinkForm')(OAuthPropertyLinkForm);
